// CostBreakdownModal.tsx

import React, { useState, useCallback } from 'react';
import { OverlayPage } from '@components/partials/OverlayPage'; 
import { CostBreakdownTable } from '@cloudclevr/components';

import { useCostBreakdownQuery, useCostBreakdownFiltersQuery } from '@hooks/roadmaps/useCostbreakdown';
import './quill-styles.css';
 
interface CostBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  roadmapId: string;
  customerName: string;
  currentPath?: string;
  categoryId?: string;
  caption?: string;
}

const CostBreakdownModal: React.FC<CostBreakdownModalProps> = ({
  isOpen,
  onClose,
  roadmapId,
  customerName,
  currentPath,
  categoryId,
  caption
}) => {
  // State for managing search and filter parameters for injected hooks
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

// Inject hooks here for separation of concerns - these will be passed to the table
const costQuery = useCostBreakdownQuery({
  roadmapId,
  searchTerm,
  categories: selectedCategories,
  enabled: !!roadmapId && isOpen
}) as any; // Type assertion for React Query v5 compatibility with component library

// Ensure filters are fetched only when the modal is open so the table
// receives category options promptly when the overlay opens.
const filtersQuery = useCostBreakdownFiltersQuery(roadmapId, isOpen) as any;

  // Callback to handle search changes from the table
  const handleSearchChange = useCallback((newSearchTerm: string, newCategories: string[]) => {
    setSearchTerm(newSearchTerm);
    setSelectedCategories(newCategories);
  }, []);

  if (!isOpen) return null;
  const defaultPath = `/roadmaps/${roadmapId || ''}`;

  return (
    <OverlayPage
      data-testid="cost-breakdown-modal"
      isFooterVisible={false}
      contentClassNames="!px-0 !pb-0"
      hasScroll={false}
      handleOnClose={onClose}
      path={currentPath && currentPath.trim() !== '' ? currentPath : defaultPath}
      headerTitle={`${(caption || 'Technology roadmap')}: proposed projects`}

    >
      <div className="h-full" data-testid="cost-breakdown-modal-content">
        {/* Move title outside of scrolling area or make it part of header */}
        
        {/* Table container with proper sticky context */}
        <div 
          className="h-full relative"
          data-testid="cost-breakdown-table-container"
        >
          <CostBreakdownTable
            roadmapId={roadmapId}
            categoryId={categoryId}
            customerName={customerName}
            injectedCostQuery={costQuery}
            injectedFiltersQuery={filtersQuery}
            onSearchChange={handleSearchChange}
            data-testid="cost-breakdown-table"
          />
        </div>
      </div>
    </OverlayPage>
  );
};

export default CostBreakdownModal;